import React from "react";
import { Button } from "react-bootstrap";
import { FILM_CONFIG } from "../film/filmConfig";

/** Shared with MissionDalle rating UI and mission slideshow Analytics bar chart (1–5). */
export const MISSION_IMAGE_RATING_LABELS = [
  "Nothing like it",
  "Slight resemblance",
  "Somewhat accurate",
  "Very close",
  "Indistinguishable",
];

/** Mission GPT prompt index for Budget allocation (Mission Budget button). */
export const MISSION_BUDGET_PROMPT_INDEX = 4;

/** Slideshow graph: display order and keys for `/get-mission-budget-averages` fields. */
export const MISSION_BUDGET_GRAPH_SYSTEMS = [
  { key: "power", label: "Power" },
  { key: "communication", label: "Communication" },
  { key: "launch", label: "Launch" },
  { key: "scientific", label: "Scientific" },
  { key: "navigation", label: "Navigation" },
];

export const MISSION_CONFIG = {
  ...FILM_CONFIG,
  workshopType: 'mission',
  pageTitle: 'AI Space Mission | Artificial Intelligence Workshop | STAGE ONE EDUCATION',
  heading: 'AI Space Mission',
  slideshowUrl: '/ai-mission-slideshow',
  footerBlurb: 'Created by participants using leading AI models',
  nodePageClass: 'intro-page-background-mission',

  tabs: {
    poster: 'Poster',
  },

  storageKeys: {
    node: 'mission-nn-node',
    workshopId: 'mission-workshop-id',
    password: 'mission-frontend-password',
    gptMessages: 'mission-gpt-messages',
    dalleOutputs: 'mission-dalle-outputs',
    musicgenUrls: 'mission-musicgen-urls',
    slide: 'mission-slide',
    readyForPoster: 'mission-ready-for-poster',
    imageGenerationStarted: 'mission-image-generation-started',
    voices: 'mission-voices',
    synth: 'mission-synth',
    missionData: 'mission-mission-data',
  },

  dalle: {
    ...FILM_CONFIG.dalle,
    hideModButtons: true,
    placeholder: 'detailed description of your mission spacecraft, generated using GPT...',
    instructionTitle: 'This AI tool specializes in generating images from text.',
    instructionSubtitle: 'Your mission image description is displayed here. Select Go to generate your image.',
    createPosterButtonText: 'Create a spacecraft poster from this image',
    templateElements: [
      { id: 'name1', text: '<spacecraft name>', fontSize: 80, fontFamily: 'Oswald', fontWeight: 'bold', fill: '#FFFFFF', stroke: '#000000', strokeWidth: 1, textAlign: 'center', position: 'mission-tr-1' },
      { id: 'name2', text: '<rocket name>', fontSize: 44, fontFamily: 'Oswald', fill: '#FFFFFF', stroke: '#000000', strokeWidth: 1, textAlign: 'center', position: 'mission-tr-2' },
      { id: 'name3', text: '<launch date>', fontSize: 44, fontFamily: 'Oswald', fill: '#FFFFFF', stroke: '#000000', strokeWidth: 1, textAlign: 'center', position: 'mission-tr-3' },
      { id: 'title', text: '<launch location>', fontSize: 28, fontFamily: 'Oswald', fill: '#FFFFFF', stroke: '#000000', strokeWidth: 1, textAlign: 'center', position: 'mission-tr-4' },
      { id: 'subtitle', text: '<mission summary>', fontSize: 32, fontFamily: 'Oswald', fill: '#FFFFFF', stroke: '#000000', strokeWidth: 1, textAlign: 'left', position: 'mission-bl' },
    ],
    templateStrings: ['<spacecraft name>', '<rocket name>', '<launch date>', '<launch location>', '<mission summary>'],
  },

  poster: {
    ...FILM_CONFIG.poster,
    cardTitle: 'Spacecraft Poster',
    instructions: 'Add a personal touch to your AI-generated spacecraft image.',
    instructionsSubtitle: 'Use the editor to include spacecraft details, launch info, and a mission summary.',
    linkedText: 'Spacecraft poster linked to presentation',
    singleColumnForm: true,
    formLabels: { name1: 'Spacecraft:', name2: 'Rocket:', name3: 'Launch Date:', title: 'Location:', subtitle: 'Summary:' },
    formPlaceholders: { name1: '<spacecraft name>', name2: '<rocket name>', name3: '<launch date>', title: '<launch location>', subtitle: '<mission summary>' },
    formDropdownOptions: {
      title: [
        'Kennedy Space Center (Florida)',
        'Cape Canaveral Space Force Station (Florida)',
        'Vandenberg Space Force Base (California)',
        'Starbase / Boca Chica Launch Site (Texas)',
        'Pacific Spaceport Complex Alaska (Alaska)',
        'Wallops Flight Facility / Mid Atlantic Regional Spaceport (Virginia)',
      ],
    },
    nextStepTitle: "Select",
    nextStepButtonText: 'Video',
    nextStepTab: 'video',
    nextStepSuffix: 'to transform your mission image into a documentary style video clip.',
  },

  gpt: {
    ...FILM_CONFIG.gpt,
    primaryPromptCount: 7,
    primaryRowBreakAfter: 3,
    /** Tighter gap under prompt row 2 (buttons 4–7) and before the prompt editor */
    primarySecondRowPaddingBottom: "2px",
    promptButtonsSectionMarginBottom: "12px",
    promptDividerMarginTop: "16px",
    revealAfterPromptIndex: 2,
    doublePromptDivider: false,
    budgetTable: {
      title: "Next Gen Systems Analysis",
      triggerPromptIndex: 4,
      storageKey: "mission-budget-table-values",
      systems: ["Power", "Communication", "Navigation", "Scientific"],
    },
    posterModeThreshold: 3,
    /** On Text tab, after Go, scroll so the GPT output (streaming) is in view above the prompt box. */
    scrollOutputIntoViewOnGo: true,
    hideAdvancedButtons: true,
    postVideoPrompts: [
      { title: "Quiz", promptVal: ["Create a 5 question multiple choice quiz based on all of the text prompts that have been generated. Do not include an answer key."] },
      { title: "Flight Plan", promptVal: ["Describe the stages of the flight required to complete the mission."] },
      { title: "Website", promptVal: ["Write a complete HTML landing page website for this mission. Include mission overview, crew profiles, timeline, gallery section, and a countdown to launch. Use modern styling with a dark space theme, responsive design, and professional fonts. At the end, include step-by-step instructions for saving and opening the website locally on a laptop using a web browser."] },
      { title: "Training", promptVal: ["Create a detailed training plan for the mission. If the mission is crewed, include crew physical fitness requirements, spacecraft systems training, mission-specific simulations, emergency procedures, and EVA preparation. If the mission is uncrewed, include ground team training, remote operations procedures, autonomous systems testing, and contingency response drills. Include a training timeline from team selection to launch."] },
      { title: "Parts List", promptVal: ["Create a complete parts list for the spacecraft next generation design. Include all major systems: propulsion, life support, power, communications, navigation, thermal control, and structural components. For each part, list the component name, quantity, specification, cost, and supplier."] },
      { title: "Technical Drawings", promptVal: ["Describe a detailed engineering drawing of the next gen system, showing its upgraded design and key components."] },
    ],
    voiceOverPromptIndex: 2,
    elevatorPitchPromptIndex: 6,
    getPrompts: (args) => {
      const { handlePromptButtonClicked, setActiveTab, missionData } = args;
      const basePrompts = FILM_CONFIG.gpt.getPrompts(args);
      basePrompts[0] = {
        ...basePrompts[0],
        title: "Mission Overview",
        promptVal: [
          "Write an official Mission Overview for ",
          " covering the objective, destination, spacecraft, rocket, key milestones, and why it mattered. Use short sections and bullets.",
        ],
        showAfter: (
          <>
            <hr />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ backgroundColor: "white", fontSize: "large", padding: "10px 15px 15px 15px", borderRadius: "15px", border: "2px solid #f05f40", boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)" }}>
                <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>Instructions:</h3>
                <p style={{ margin: "0" }}>
                  Now, let's generate descriptions for your{" "}
                  <Button variant="outline-primary" size="sm" onClick={() => handlePromptButtonClicked(null, 1)} style={{ margin: "0 5px", padding: "4px 12px", fontSize: "14px", display: "inline-block", whiteSpace: "nowrap", flexShrink: 0, transition: "all 0.3s ease" }}>
                    2: Mission Image
                  </Button>{" "}
                  — these will be used to create images.
                </p>
              </div>
            </div>
            <hr />
          </>
        ),
      };
      basePrompts[1] = {
        ...basePrompts[1],
        title: "Mission Image",
        promptVal: [
          "Write a detailed 100 word prompt for an image generation model that depicts the original ",
          " mission with realistic, historically accurate visual detail. Include the spacecraft, mission environment, and four specific visual details from the real mission. Use a photorealistic style with a simple background.",
        ],
        showAfter: (
          <>
            <hr />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ backgroundColor: "white", fontSize: "large", padding: "10px 15px 15px 15px", borderRadius: "15px", border: "2px solid #f05f40", boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)" }}>
                <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>Instructions:</h3>
                <p style={{ margin: "0" }}>
                  Now let's generate an AI image of your historic mission.
                  <br /><br />
                  Select{" "}
                  <Button variant="outline-primary" size="sm" onClick={() => setActiveTab("dalle")} style={{ margin: "0 5px", padding: "4px 12px", fontSize: "14px", display: "inline-block", whiteSpace: "nowrap", flexShrink: 0, transition: "all 0.3s ease", borderColor: "#f05f40", color: "#f05f40" }}>
                    Image
                  </Button>
                </p>
              </div>
            </div>
            <hr />
          </>
        ),
      };
      basePrompts.splice(2, 1);
      basePrompts[2] = {
        ...basePrompts[2],
        title: "Mission Transmission",
        promptVal: [
          "Write a realistic mission control transmission from Houston about a problem during the ",
          " mission. Use present tense and authentic spaceflight communication style. Include the problem, the affected system, and the immediate procedure or solution. Exactly 50 words.",
        ],
      };
      const nextGenBase = basePrompts[3];
      const imageBase = basePrompts[5];
      const mName = missionData?.name ?? "<mission name>";
      const mSummary = missionData?.significance ?? "<mission summary>";
      basePrompts[3] = {
        ...nextGenBase,
        title: "Mission Design",
        fillIn: "Type your Next Gen mission objective here...",
        wrapFillInSlot: true,
        promptVal: [
          `You are leading the Next Gen ${mName} mission. Building on the ${mSummary}, describe your mission's primary objective.\n\n`,
          `<br /><br /><span class="gpt-prompt-hint-muted">AI: Describe the Next Gen technologies required for the power, communication, navigation, scientific, and launch systems your mission needs to succeed.</span>`,
        ],
      };
      basePrompts[4] = {
        ...nextGenBase,
        title: "Mission Budget",
        budgetAllocation: true,
        fillIn: undefined,
        promptVal: [""],
      };
      basePrompts[5] = {
        ...nextGenBase,
        title: "ROI Analysis",
        fillIn: undefined,
        wrapFillInSlot: false,
        promptVal: [
          `The future of the Next Gen ${mName} mission is in your hands. As mission director, you must persuade Congress to approve funding for your project.<br /><span class="gpt-fill-in-slot gpt-roi-fill-box" data-fill-in-encoded="${encodeURIComponent("Explain why your mission deserves funding, what it will achieve, and why the investment is worth it...")}">Explain why your mission deserves funding, what it will achieve, and why the investment is worth it...</span><br /><span class="gpt-prompt-hint-muted">AI: You are a U.S. congressional funding committee. Review the mission proposal, explain your reasoning, and make a Yes or No funding decision.</span>`,
        ],
      };
      basePrompts[6] = { ...imageBase, title: "Mission Image" };
      basePrompts.splice(7);
      return basePrompts;
    },
  },

  slideshow: {
    title: 'Space Mission - Slideshow',
    onImageRatingClick: null,
    onBudgetBreakdownClick: null,
  },
};
